import React from 'react';
import type {
  CSSProperties, ReactNode, InputHTMLAttributes, TextareaHTMLAttributes, ChangeEventHandler
} from 'react';
import useStyles from '../../theme/useStyles';

// Label + control + error message, in the house style.
// Works for text, number, date, time, email, password and textarea.
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'style' | 'type' | 'onChange' | 'value'> {
  label?: ReactNode;
  name?: string;
  value?: string | number;
  onChange?: ChangeEventHandler<HTMLInputElement & HTMLTextAreaElement>;
  /** 'textarea' swaps the <input> for a <textarea>. */
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  /** Span the full width of a FormLayout.Grid. */
  fullWidth?: boolean;
  rows?: number;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
  children?: ReactNode;
}

const Input = ({
  label,
  name,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  hint,
  fullWidth = false,
  rows,
  style,
  containerStyle,
  isDarkMode,
  children,
  maxLength,
  ...rest
}: InputProps) => {
  const s = useStyles(isDarkMode);
  const isTextarea = type === 'textarea';
  // The browser stops accepting input at maxLength without saying anything, so
  // a field with a limit shows the count and names the limit once it is
  // reached (CHM-111 / CHM-112 / CHM-113).
  const charCount = String(value ?? '').length;
  const atLimit = typeof maxLength === 'number' && charCount >= maxLength;

  const controlStyle = {
    ...s.input,
    ...(error ? { borderColor: s.t.error } : {}),
    ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
    ...style
  };

  return (
    <div style={{ ...s.inputGroup, ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}>
      {label && (
        <label htmlFor={name} style={s.label}>
          {label}
          {required && <span style={{ color: s.t.error, marginLeft: '4px' }}>*</span>}
        </label>
      )}

      {isTextarea ? (
        // `rest` is typed against <input>; a <textarea>'s event handlers carry
        // a different element type, so it is re-cast for this branch.
        <textarea
          id={name} name={name} value={value ?? ''} onChange={onChange}
          placeholder={placeholder} required={required} disabled={disabled}
          rows={rows || 3} maxLength={maxLength}
          style={{ ...controlStyle, resize: 'vertical', fontFamily: 'inherit' }}
          {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          id={name} name={name} type={type} value={value ?? ''} onChange={onChange}
          placeholder={placeholder} required={required} disabled={disabled}
          maxLength={maxLength}
          style={controlStyle}
          {...rest}
        />
      )}

      {children}
      {typeof maxLength === 'number' && (
        // Reaching the limit is allowed, not an error: the count stays, with a
        // quiet note that no more characters fit.
        <span style={{
          fontSize: s.t.fontSize.sm, marginTop: '4px', alignSelf: 'flex-end',
          color: atLimit ? s.t.warning : s.t.textFaint,
          fontWeight: atLimit ? 'bold' : 'normal'
        }}>
          {atLimit ? `${charCount} / ${maxLength} — maximum reached` : `${charCount} / ${maxLength}`}
        </span>
      )}
      {hint && !error && <span style={{ fontSize: s.t.fontSize.sm, color: s.t.textFaint, marginTop: '4px' }}>{hint}</span>}
      {error && <span style={{ fontSize: s.t.fontSize.sm, color: s.t.error, marginTop: '4px' }}>{error}</span>}
    </div>
  );
};

export default Input;
